import React, { useEffect } from "react";
import { IMAGE_PATHS } from "../../constants/imagePaths";

interface WinterSceneProps {
  seasonStart?: Date;
  seasonEnd?: Date;
}

const formatDate = (date: Date): string => {
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export const WinterScene: React.FC<WinterSceneProps> = ({
  seasonStart,
  seasonEnd,
}) => {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  // Format season dates for display
  const formattedSeasonStart = seasonStart ? formatDate(seasonStart) : null;
  const formattedSeasonEnd = seasonEnd ? formatDate(seasonEnd) : null;

  return (
    <div
      className="relative h-screen w-full overflow-hidden bg-black"
      role="main"
      aria-label="Winter scene - Bookings are currently unavailable"
    >
      {/* Background skating video with slow zoom-out effect */}
      <video
        className="absolute inset-0 h-full w-full object-cover"
        src={IMAGE_PATHS.skating_man_video}
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
      />

      {/* Optional dark overlay for better text readability */}
      <div className="absolute inset-0 bg-black/40" aria-hidden="true" />

      {/* Title and booking message */}
      <div className="relative z-10 flex h-full flex-col items-center justify-center gap-6 px-4 text-center">
        <h1
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold 
            bg-gradient-to-r from-white via-blue-100 to-white bg-clip-text text-transparent
            drop-shadow-[0_0_15px_rgba(255,255,255,0.5)]
            [text-shadow:0_0_30px_rgba(147,197,253,0.5),0_0_60px_rgba(147,197,253,0.3)]"
          style={{
            fontFamily: "'Mulish', sans-serif",
          }}
        >
          Hakuba Snow Sports School
        </h1>

        {/* Booking availability message */}
        {formattedSeasonStart && formattedSeasonEnd && (
          <div>
            <p
              className="text-lg sm:text-xl md:text-2xl text-white/90 font-medium
                drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]"
              style={{
                fontFamily: "'Mulish', sans-serif",
              }}
            >
              Booking opens from{" "}
              <span
                className="text-white-200 font-bold text-3xl"
                style={{
                  textShadow: "3px 2px 0 #000",
                }}
              >
                {formattedSeasonStart}
              </span>{" "}
              to{" "}
              <span
                className="text-white-200 font-bold text-3xl"
                style={{
                  textShadow: "3px 2px 0 #000",
                }}
              >
                {formattedSeasonEnd}
              </span>
            </p>
            <p
              className="mt-2 text-base sm:text-lg text-white/70 font-normal
                drop-shadow-[0_1px_2px_rgba(0,0,0,0.2)]"
              style={{
                fontFamily: "'Mulish', sans-serif",
              }}
            >
              Stay updated!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WinterScene;
